import React, { useState } from 'react';
import { StarIcon, MapPinIcon, PlaneIcon, TagIcon, FilterIcon, SortAscIcon } from 'lucide-react';
import { Package } from '../../utils/types';
import Button from '../common/Button';
import { useNavigate } from 'react-router-dom';
interface RecommendedPackagesListProps {
  packages: Package[];
}
const RecommendedPackagesList: React.FC<RecommendedPackagesListProps> = ({
  packages
}) => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('price');
  const [priceFilter, setPriceFilter] = useState<[number, number]>([0, 5000]);
  const [starFilter, setStarFilter] = useState<number[]>([]);
  // Handle booking a package
  const handleBookPackage = (pkg: Package) => {
    navigate(`/booking/review?packageId=${pkg.id}`);
  };
  // Format the departure and arrival times
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Sort packages based on selected criteria
  const sortedPackages = [...packages].sort((a, b) => {
    if (sortBy === 'price') {
      return a.totalPrice - b.totalPrice;
    } else if (sortBy === 'rating') {
      return b.hotel.stars - a.hotel.stars;
    } else if (sortBy === 'savings') {
      return b.savings - a.savings;
    }
    return 0;
  });
  // Filter packages
  const filteredPackages = sortedPackages.filter(pkg => {
    // Apply price filter
    if (pkg.totalPrice < priceFilter[0] || pkg.totalPrice > priceFilter[1]) {
      return false;
    }
    // Apply star filter if any stars are selected
    if (starFilter.length > 0 && !starFilter.includes(pkg.hotel.stars)) {
      return false;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-463">
      <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-464">
        <div className="flex justify-between items-center" data-id="element-465">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-466">
            ✨ Recommended Packages
          </h2>
          <div className="flex items-center" data-id="element-467">
            <SortAscIcon className="h-5 w-5 text-gray-500 mr-2" data-id="element-468" />
            <select className="text-sm border-gray-300 rounded-md" value={sortBy} onChange={e => setSortBy(e.target.value)} data-id="element-469">
              <option value="price" data-id="element-470">Price: Low to High</option>
              <option value="price-desc" data-id="element-471">Price: High to Low</option>
              <option value="rating" data-id="element-472">Rating: High to Low</option>
              <option value="savings" data-id="element-473">Best Savings</option>
            </select>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-474">
        {filteredPackages.map(pkg => <div key={pkg.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-475">
            <div className="relative" data-id="element-476">
              <div className="absolute top-0 left-0 bg-blue-600 text-white py-1 px-3 rounded-br-lg" data-id="element-477">
                <div className="flex items-center" data-id="element-478">
                  <TagIcon className="h-4 w-4 mr-1" data-id="element-479" />
                  <span className="text-sm font-medium" data-id="element-480">
                    Save ${pkg.savings}
                  </span>
                </div>
              </div>
              <img src={pkg.hotel.thumbnail} alt={pkg.hotel.name} className="w-full h-48 object-cover" data-id="element-481" />
            </div>
            <div className="p-6" data-id="element-482">
              <div className="flex justify-between items-start" data-id="element-483">
                <div data-id="element-484">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-485">
                    {pkg.hotel.name}
                  </h3>
                  <div className="flex items-center mt-1 mb-2" data-id="element-486">
                    <div className="flex mr-2" data-id="element-487">
                      {Array(5).fill(0).map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < pkg.hotel.stars ? 'text-yellow-400' : 'text-gray-300'}`} fill={i < pkg.hotel.stars ? 'currentColor' : 'none'} data-id="element-488" />)}
                    </div>
                    <span className="text-sm text-gray-500" data-id="element-489">
                      {pkg.hotel.stars}-star hotel
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mb-4" data-id="element-490">
                    <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-491" />
                    <span data-id="element-492">
                      {pkg.hotel.city}, {pkg.hotel.country}
                    </span>
                  </div>
                </div>
                <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm" data-id="element-493">
                  Best Value
                </div>
              </div>
              <div className="border-t border-b border-gray-200 py-4 my-4" data-id="element-494">
                <div className="flex items-start" data-id="element-495">
                  <PlaneIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" data-id="element-496" />
                  <div data-id="element-497">
                    <h4 className="font-medium text-gray-900" data-id="element-498">
                      Flight Details
                    </h4>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-499">
                      {pkg.flight.airline} • Flight {pkg.flight.flightNumber}
                    </p>
                    <div className="flex items-center text-sm mt-2" data-id="element-500">
                      <div className="mr-3" data-id="element-501">
                        <p className="font-medium" data-id="element-502">
                          {formatTime(pkg.flight.departureTime)}
                        </p>
                        <p className="text-gray-500" data-id="element-503">
                          {pkg.flight.departureAirport}
                        </p>
                      </div>
                      <div className="border-t border-gray-300 w-8 mx-2" data-id="element-504"></div>
                      <div data-id="element-505">
                        <p className="font-medium" data-id="element-506">
                          {formatTime(pkg.flight.arrivalTime)}
                        </p>
                        <p className="text-gray-500" data-id="element-507">
                          {pkg.flight.arrivalAirport}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-508">
                <div className="mb-4 md:mb-0" data-id="element-509">
                  <div className="flex items-baseline" data-id="element-510">
                    <span className="text-2xl font-bold text-blue-600" data-id="element-511">
                      ${pkg.totalPrice}
                    </span>
                    <span className="text-gray-500 text-sm ml-1" data-id="element-512">total</span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-513">
                    Flight + 7 nights
                  </span>
                </div>
                <Button variant="primary" onClick={() => handleBookPackage(pkg)} data-id="element-514">
                  Book Now
                </Button>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
};
export default RecommendedPackagesList;